import { Star, ExternalLink } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { GOOGLE_REVIEW_URL } from "@/lib/site";

interface ReviewCtaProps {
  /**
   * "section" (default) – full-width bordered block, fits below page content.
   * "inline"            – compact card, fits next to other CTAs / asides.
   */
  variant?: "section" | "inline";
  className?: string;
}

/**
 * Google-compliant review CTA.
 *
 * Renders NOTHING while GOOGLE_REVIEW_URL is empty so we never ship a broken
 * review link. Copy is deliberately neutral — no incentives, no gating, no
 * scripted answer content, no request to name employees. Every visitor sees
 * the same open invitation, in line with Google's Business Profile review
 * policy (2026 update).
 */
export function ReviewCta({ variant = "section", className = "" }: ReviewCtaProps) {
  const { lang } = useI18n();
  const de = lang === "de";

  if (!GOOGLE_REVIEW_URL) return null;

  const heading = de ? "Erfahrung mit uns teilen" : "Share your experience with us";
  const body = de
    ? "Wenn wir Ihnen bei Ihrer Recherche oder Beratung geholfen haben, freuen wir uns über eine ehrliche Bewertung auf Google – positiv wie kritisch. Ihre Rückmeldung hilft anderen Interessenten bei der Orientierung."
    : "If we helped with your research or consultation, we appreciate an honest review on Google — positive or critical. Your feedback helps other visitors get their bearings.";
  const cta = de ? "Bewertung auf Google schreiben" : "Write a review on Google";
  const note = de
    ? "Kein Anreiz, kein Filter – jeder Besucher sieht dieselbe offene Einladung."
    : "No incentive, no filter — every visitor sees the same open invitation.";

  if (variant === "inline") {
    return (
      <aside
        aria-labelledby="review-cta-heading"
        className={
          "rounded-2xl border border-border bg-card p-5 " + className
        }
      >
        <div className="flex items-center gap-2 text-amber-500" aria-hidden="true">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star key={i} className="h-4 w-4 fill-current" />
          ))}
        </div>
        <h2
          id="review-cta-heading"
          className="mt-3 text-base font-semibold text-foreground"
        >
          {heading}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">{body}</p>
        <a
          href={GOOGLE_REVIEW_URL}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="mt-4 inline-flex items-center gap-2 rounded-md bg-indigo px-4 py-2 text-sm font-medium text-indigo-foreground hover:opacity-90"
        >
          {cta}
          <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
        </a>
        <p className="mt-3 text-[11px] text-muted-foreground">{note}</p>
      </aside>
    );
  }

  return (
    <section
      aria-labelledby="review-cta-heading"
      className={
        "mt-10 overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-amber-500/5 via-background to-indigo/5 p-6 md:p-8 " +
        className
      }
    >
      <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
        <div className="max-w-2xl">
          <div className="flex items-center gap-2 text-amber-500" aria-hidden="true">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star key={i} className="h-4 w-4 fill-current" />
            ))}
          </div>
          <h2
            id="review-cta-heading"
            className="mt-3 font-display text-2xl leading-tight text-foreground md:text-3xl"
          >
            {heading}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground md:text-base">{body}</p>
          <p className="mt-3 text-[11px] text-muted-foreground">{note}</p>
        </div>
        <a
          href={GOOGLE_REVIEW_URL}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-indigo px-5 py-3 text-sm font-medium text-indigo-foreground hover:opacity-90"
        >
          {cta}
          <ExternalLink aria-hidden="true" className="h-4 w-4" />
        </a>
      </div>
    </section>
  );
}
